import React, { useState, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import ErrorIcon from '@mui/icons-material/Error'
import { CSVLink } from 'react-csv'
import { httpsCallable } from 'firebase/functions'
import { firebase_functions } from '../../config/firebaseConfig'
import {
  AutoTradeReportRow,
  AutoTradeReportSummary,
  AutoTradeReportResponse,
  AutoTradeReportGenerationStatus,
} from '../types/AutoTradeReport'
import TradesDownloadButton from '../components/autoTrade/TradesDownloadButton'
import {
  pageContainer,
  pageHeader,
  pageTitle,
  pageSubtitle,
  sectionCard,
  sectionHeader,
  sectionTitle,
  filterBar,
  tableHeadCell,
  tableBodyCell,
  outlinedButton,
  primaryButton,
  statValue,
  statLabel,
  loadingContainer,
  emptyState,
  adminColors,
} from '../adminStyles'

const formatDate = (dateString: string): string => {
  if (dateString === 'Unknown') return 'Unknown'
  try {
    const date = new Date(dateString)
    return date.toLocaleString()
  } catch {
    return dateString
  }
}

const getStatusColor = (
  status: AutoTradeReportRow['status']
): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case 'PROFITABLE':
      return 'success'
    case 'LOSS':
      return 'error'
    case 'NO_TRADES':
      return 'warning'
    case 'ERROR':
      return 'error'
    default:
      return 'default'
  }
}

type SortableColumn =
  | 'totalTrades'
  | 'totalProfit'
  | 'profitPercent'
  | 'winningTrades'
  | 'losingTrades'
  | 'winRate'
  | 'pattern'
type SortDirection = 'asc' | 'desc'

export default function AutoTradeReport() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<AutoTradeReportRow[]>([])
  const [summary, setSummary] = useState<AutoTradeReportSummary | null>(null)
  const [lastGeneratedAt, setLastGeneratedAt] = useState<string | null>(null)
  const [generationStatus, setGenerationStatus] = useState<AutoTradeReportGenerationStatus | null>(
    null
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [csvData, setCsvData] = useState<any[][]>([[]])
  const csvLinkRef = React.useRef<any>(null)
  const isFetchingRef = React.useRef(false)

  const [leaderboardSyncing, setLeaderboardSyncing] = useState(false)
  const [leaderboardSyncResult, setLeaderboardSyncResult] = useState<string | null>(null)

  const getAutoTradeReportFunc = React.useMemo(
    () =>
      httpsCallable<
        { forceRefresh?: boolean },
        { data: AutoTradeReportResponse & { generationStatus: AutoTradeReportGenerationStatus } }
      >(firebase_functions, 'getAutoTradeReport'),
    []
  )

  const syncLeaderboardFunc = React.useMemo(
    () =>
      httpsCallable<void, { rows: number; generatedAt: string }>(
        firebase_functions,
        'syncLeaderboard'
      ),
    []
  )

  const handleSyncLeaderboard = useCallback(async () => {
    setLeaderboardSyncing(true)
    setLeaderboardSyncResult(null)
    try {
      const result = await syncLeaderboardFunc()
      const { rows, generatedAt } = result.data
      setLeaderboardSyncResult(`Leaderboard synced — ${rows} rows as of ${formatDate(generatedAt)}`)
    } catch (err: any) {
      setLeaderboardSyncResult(`Sync failed: ${err.message || err.code || 'Unknown error'}`)
    } finally {
      setLeaderboardSyncing(false)
    }
  }, [syncLeaderboardFunc])

  const fetchReport = useCallback(
    async (forceRefresh?: boolean) => {
      if (isFetchingRef.current) {
        return
      }

      try {
        isFetchingRef.current = true
        setLoading(true)
        setError(null)

        const result = await getAutoTradeReportFunc(forceRefresh ? { forceRefresh: true } : {})
        const response = result.data

        if (response && response.data) {
          setData(response.data.rows)
          setSummary(response.data.summary)
          setLastGeneratedAt(
            response.data.generationStatus?.lastGeneratedAt || response.data.generatedAt
          )
          setGenerationStatus(response.data.generationStatus || null)
        } else {
          throw new Error('Invalid response format from server')
        }
      } catch (err: any) {
        console.error('Error fetching report:', err)
        setError(err.message || err.code || 'Failed to fetch report. Please try again.')
        setData([])
        setSummary(null)
        setGenerationStatus(null)
      } finally {
        setLoading(false)
        isFetchingRef.current = false
      }
    },
    [getAutoTradeReportFunc]
  )

  React.useEffect(() => {
    fetchReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredData = useMemo(() => {
    let filtered = [...data]

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter((row) => row.status === statusFilter)
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (row) =>
          row.name.toLowerCase().includes(query) ||
          row.userEmail.toLowerCase().includes(query) ||
          row.userGcid.toLowerCase().includes(query)
      )
    }

    return filtered
  }, [data, statusFilter, searchQuery])

  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData

    const sorted = [...filteredData].sort((a, b) => {
      if (sortColumn === 'pattern') {
        const cmp = a.patternName.localeCompare(b.patternName)
        return sortDirection === 'asc' ? cmp : -cmp
      }

      let aValue: number
      let bValue: number

      switch (sortColumn) {
        case 'totalTrades':
          aValue = a.totalTrades
          bValue = b.totalTrades
          break
        case 'totalProfit':
          aValue = a.totalProfit
          bValue = b.totalProfit
          break
        case 'winningTrades':
          aValue = a.winningTrades
          bValue = b.winningTrades
          break
        case 'losingTrades':
          aValue = a.losingTrades
          bValue = b.losingTrades
          break
        case 'profitPercent':
          aValue = a.profitPercent
          bValue = b.profitPercent
          break
        case 'winRate':
          aValue = a.winRate
          bValue = b.winRate
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [filteredData, sortColumn, sortDirection])

  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const prepareCSV = useCallback(() => {
    const headers = [
      'Name',
      'User Email',
      'User GCID',
      'Phone Number',
      'Total Trades',
      'Total Profit',
      'Starting Equity',
      'Profit %',
      'Winning Trades',
      'Losing Trades',
      'Win Rate (%)',
      'First Trade Date',
      'Last Trade Date',
      'Server',
      'Trading House',
      'Pattern Name',
      'Alert Name',
      'Alert ID',
      'Status',
      'Error Message',
    ]

    const rows = sortedData.map((row) => [
      row.name,
      row.userEmail,
      row.userGcid,
      row.phoneNumber,
      row.totalTrades,
      row.totalProfit,
      row.startingEquity,
      row.profitPercent,
      row.winningTrades,
      row.losingTrades,
      row.winRate,
      formatDate(row.firstTradeDate),
      formatDate(row.lastTradeDate),
      row.server || '',
      row.tradingHouse,
      row.patternName,
      row.alertName,
      row.alertId || '',
      row.status,
      row.errorMessage || '',
    ])

    setCsvData([headers, ...rows])
  }, [sortedData])

  const handleExportCSV = useCallback(() => {
    prepareCSV()
    setTimeout(() => {
      csvLinkRef.current?.link?.click()
    }, 100)
  }, [prepareCSV])

  const sortableHeaderCell = (label: string, column: SortableColumn) => (
    <TableCell align="right" sx={tableHeadCell}>
      <TableSortLabel
        active={sortColumn === column}
        direction={sortColumn === column ? sortDirection : 'asc'}
        onClick={() => handleSort(column)}
        sx={{ '& .MuiTableSortLabel-icon': { fontSize: 14 } }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  )

  return (
    <Box sx={pageContainer}>
      {/* Header */}
      <Box sx={pageHeader}>
        <Box>
          <Typography sx={pageTitle}>Auto Trade Report</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 0.5 }}>
            {lastGeneratedAt && (
              <Typography sx={pageSubtitle}>
                Last generated: {formatDate(lastGeneratedAt)}
              </Typography>
            )}
            {generationStatus?.isGenerating && (
              <Typography sx={{ ...pageSubtitle, color: adminColors.warningAmber }}>
                Report generating
                {generationStatus.generationStartedAt
                  ? ` (started ${formatDate(generationStatus.generationStartedAt)})`
                  : ''}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={() => fetchReport()}
              disabled={loading}
              sx={outlinedButton}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              onClick={() => fetchReport(true)}
              disabled={loading || generationStatus?.isGenerating}
              sx={primaryButton}
            >
              Generate
            </Button>
            <Button
              variant="outlined"
              onClick={handleSyncLeaderboard}
              disabled={leaderboardSyncing || !data.length}
              startIcon={
                leaderboardSyncing ? (
                  <CircularProgress size={14} sx={{ color: 'inherit' }} />
                ) : undefined
              }
              sx={outlinedButton}
            >
              {leaderboardSyncing ? 'Syncing…' : 'Sync Leaderboard'}
            </Button>
            {sortedData.length > 0 && (
              <Button
                variant="outlined"
                onClick={handleExportCSV}
                disabled={loading}
                sx={outlinedButton}
              >
                Export CSV
              </Button>
            )}
          </Box>
          {leaderboardSyncResult && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: leaderboardSyncResult.startsWith('Sync failed')
                  ? adminColors.lossRed
                  : adminColors.profitGreen,
              }}
            >
              {leaderboardSyncResult}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Summary Stats */}
      {summary && !loading && (
        <Box sx={{ display: 'flex', gap: 2.5 }}>
          {[
            { value: summary.totalPlans, label: 'Total Plans' },
            { value: summary.profitable, label: 'Profitable', color: adminColors.profitGreen },
            { value: summary.loss, label: 'Loss', color: adminColors.lossRed },
            { value: summary.noTrades, label: 'No Trades', color: adminColors.warningAmber },
            { value: summary.error, label: 'Errors', color: adminColors.lossRed },
          ].map((stat) => (
            <Box key={stat.label} sx={{ ...sectionCard, px: 2.5, py: 1.5, flex: 1 }}>
              <Typography
                sx={{
                  ...statValue,
                  fontSize: '1.25rem',
                  color: stat.color || adminColors.textPrimary,
                }}
              >
                {stat.value}
              </Typography>
              <Typography sx={statLabel}>{stat.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Filters */}
      {!loading && data.length > 0 && (
        <Box sx={sectionCard}>
          <Box sx={filterBar}>
            <TextField
              size="small"
              placeholder="Search by name, email, or GCID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: adminColors.textMuted, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 280,
                flex: '1 1 280px',
                '& .MuiOutlinedInput-root': { fontSize: '0.85rem', borderRadius: '6px' },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel sx={{ fontSize: '0.85rem' }}>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
                sx={{ fontSize: '0.85rem', borderRadius: '6px' }}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="PROFITABLE">Profitable</MenuItem>
                <MenuItem value="LOSS">Loss</MenuItem>
                <MenuItem value="NO_TRADES">No Trades</MenuItem>
                <MenuItem value="ERROR">Error</MenuItem>
              </Select>
            </FormControl>
            <Typography sx={{ ...pageSubtitle, ml: 'auto' }}>
              {sortedData.length} of {data.length} rows
            </Typography>
          </Box>
        </Box>
      )}

      {/* Error */}
      {error && !loading && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => fetchReport()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={loadingContainer}>
          <CircularProgress size={28} sx={{ color: adminColors.accent }} />
        </Box>
      )}

      {/* Table */}
      {!loading && !error && sortedData.length > 0 && (
        <Box sx={sectionCard}>
          <TableContainer
            sx={{
              maxHeight: 'calc(100vh - 420px)',
              overflowX: 'auto',
              overflowY: 'auto',
            }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCell}>Name</TableCell>
                  <TableCell sx={tableHeadCell}>Email</TableCell>
                  <TableCell sx={tableHeadCell}>GCID</TableCell>
                  <TableCell sx={tableHeadCell}>Phone</TableCell>
                  {sortableHeaderCell('Total Trades', 'totalTrades')}
                  {sortableHeaderCell('Total Profit', 'totalProfit')}
                  <TableCell align="right" sx={tableHeadCell}>
                    Starting Equity
                  </TableCell>
                  {sortableHeaderCell('Profit %', 'profitPercent')}
                  {sortableHeaderCell('Winning', 'winningTrades')}
                  {sortableHeaderCell('Losing', 'losingTrades')}
                  {sortableHeaderCell('Win Rate', 'winRate')}
                  <TableCell sx={tableHeadCell}>First Trade</TableCell>
                  <TableCell sx={tableHeadCell}>Last Trade</TableCell>
                  <TableCell sx={tableHeadCell}>Server</TableCell>
                  <TableCell sx={tableHeadCell}>House</TableCell>
                  {sortableHeaderCell('Pattern', 'pattern')}
                  <TableCell sx={tableHeadCell}>Alert</TableCell>
                  <TableCell sx={tableHeadCell}>Status</TableCell>
                  <TableCell sx={tableHeadCell}>Trades</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={tableBodyCell}>{row.name || 'N/A'}</TableCell>
                    <TableCell sx={tableBodyCell}>{row.userEmail}</TableCell>
                    <TableCell
                      sx={{ ...tableBodyCell, fontFamily: 'monospace', fontSize: '0.775rem' }}
                    >
                      {row.userGcid}
                    </TableCell>
                    <TableCell sx={tableBodyCell}>{row.phoneNumber || 'N/A'}</TableCell>
                    <TableCell align="right" sx={tableBodyCell}>
                      {row.totalTrades}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableBodyCell,
                        fontWeight: 500,
                        color: row.totalProfit >= 0 ? adminColors.profitGreen : adminColors.lossRed,
                      }}
                    >
                      ${row.totalProfit.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={tableBodyCell}>
                      {row.startingEquity > 0 ? `$${row.startingEquity.toFixed(2)}` : 'N/A'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableBodyCell,
                        fontWeight: 500,
                        color:
                          row.profitPercent > 0
                            ? adminColors.profitGreen
                            : row.profitPercent < 0
                              ? adminColors.lossRed
                              : undefined,
                      }}
                    >
                      {row.startingEquity > 0 ? `${row.profitPercent.toFixed(2)}%` : 'N/A'}
                    </TableCell>
                    <TableCell align="right" sx={tableBodyCell}>
                      {row.winningTrades}
                    </TableCell>
                    <TableCell align="right" sx={tableBodyCell}>
                      {row.losingTrades}
                    </TableCell>
                    <TableCell align="right" sx={tableBodyCell}>
                      {row.winRate}%
                    </TableCell>
                    <TableCell sx={tableBodyCell}>{formatDate(row.firstTradeDate)}</TableCell>
                    <TableCell sx={tableBodyCell}>{formatDate(row.lastTradeDate)}</TableCell>
                    <TableCell sx={tableBodyCell}>{row.server || 'N/A'}</TableCell>
                    <TableCell sx={tableBodyCell}>{row.tradingHouse}</TableCell>
                    <TableCell sx={tableBodyCell}>{row.patternName}</TableCell>
                    <TableCell sx={tableBodyCell}>{row.alertName}</TableCell>
                    <TableCell sx={tableBodyCell}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={row.status}
                          color={getStatusColor(row.status)}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 22 }}
                        />
                        {row.errorMessage && (
                          <Tooltip title={row.errorMessage} arrow>
                            <IconButton size="small" color="error">
                              <ErrorIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={tableBodyCell}>
                      <TradesDownloadButton row={row} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Empty State */}
      {!loading && !error && sortedData.length === 0 && data.length === 0 && (
        <Box sx={emptyState}>
          <Typography
            sx={{ fontSize: '0.95rem', fontWeight: 500, color: adminColors.textSecondary, mb: 0.5 }}
          >
            No data available
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: adminColors.textMuted }}>
            Click Generate to create a new report.
          </Typography>
        </Box>
      )}

      {/* No filter results */}
      {!loading && !error && sortedData.length === 0 && data.length > 0 && (
        <Box sx={emptyState}>
          <Typography
            sx={{ fontSize: '0.95rem', fontWeight: 500, color: adminColors.textSecondary, mb: 0.5 }}
          >
            No results match your filters
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: adminColors.textMuted }}>
            Try adjusting your search or status filter.
          </Typography>
        </Box>
      )}

      {/* Hidden CSV Link */}
      <CSVLink
        data={csvData}
        filename={`auto-trade-report-${new Date().toISOString().split('T')[0]}.csv`}
        style={{ visibility: 'hidden' }}
        ref={csvLinkRef}
      />
    </Box>
  )
}
